import { useTranslation } from 'react-i18next';
import { Wallet } from 'lucide-react';
import { formatMoney, formatMoneyExplicit } from '@/lib/money';
import NamedIcon from '@/components/ui/NamedIcon';
import type { WalletPageState } from './walletPageState';
import styles from './WalletPage.module.css';

interface Props {
  view: WalletPageState;
  minimumBalanceCents: number;
}

/**
 * One balance card per wallet, each in its own currency (2026-10-25). Every
 * wallet the driver has shows whatever the "Top up in" toggle says; the
 * toggle only marks the wallet a top-up goes to ("Top-up goes here").
 */
export function WalletBalanceList({ view, minimumBalanceCents }: Props) {
  const { t, i18n } = useTranslation('wallet');
  return (
    <>
      {view.multiCurrency && <h2 className={styles.cardTitle}>{t('wallets.title')}</h2>}
      <div className={styles.walletList}>
        {view.wallets.map((w) => {
          const target = view.multiCurrency && w.currency === view.topUpCurrency;
          return (
            <section
              key={w.currency}
              className={[styles.balanceCard, target ? styles.balanceCardActive : ''].filter(Boolean).join(' ')}
              data-wallet-currency={w.currency}
              data-topup-target={target ? 'true' : undefined}
            >
              <span className={styles.balanceIcon}>
                <NamedIcon name="wallet-hero" fallback={<Wallet size={22} />} size={22} />
              </span>
              <div className={styles.balanceText}>
                <span className={styles.balanceLabelRow}>
                  <span className={styles.balanceLabel}>
                    {view.multiCurrency ? t('wallets.label', { currency: w.currency }) : t('balance.label')}
                  </span>
                  {target && <span className={styles.targetBadge}>{t('wallets.target')}</span>}
                </span>
                <span className={styles.balanceValue}>
                  {/* The code beside it says which dollar: "$40.00 USD", "CA$32.00 CAD". */}
                  {formatMoney(w.balanceCents, w.currency, i18n.language)}
                  <span className={styles.balanceCurrency}> {w.currency}</span>
                </span>
                <span className={styles.balanceMeta}>
                  {view.multiCurrency
                    ? t(`wallets.usedFor.${w.currency === 'CAD' ? 'CAD' : 'USD'}`)
                    : t('balance.minToCharge', { amount: formatMoneyExplicit(minimumBalanceCents, w.currency, i18n.language) })}
                </span>
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}
